/**
 * Pickld collection page - filter interactions (2026-07-03).
 * Docs: docs/components/collection-page.md ("The script").
 *
 * What it does:
 *
 * 1. Show more / show fewer toggle for long filter value lists
 *    (the section renders the first 6 values + a "Show all N" button).
 *
 * 2. AJAX "Apply filters" - intercepts the form submit, fetches the
 *    collection page for the new URL via Shopify's ?section_id=...
 *    Section Rendering endpoint, and swaps in place the product grid
 *    wrap (which also holds the pagination), the product count, and each
 *    facet value's count and disabled state (matched by position). No
 *    full page reload. history.pushState() updates the URL so the filter
 *    state is shareable / bookmarkable / back-button-safe.
 *
 * 3. Instant apply (2026-07-03) - a checkbox change applies after 100ms,
 *    a price input after 600ms; the form then carries .is-instant-mode,
 *    which hides the Apply button.
 *
 * 4. Skill level routing (2026-07-03) - checking Starter, Intermediate or
 *    Professional navigates to that skill's collection instead of adding a
 *    query param, keeping the other filter params. Sub-collection URLs like
 *    /collections/intermediate-paddles are separate SEO landing pages.
 *
 * 5. Back and forward - popstate re-fetches and swaps for the current URL.
 *
 * The sort select and the phone filter drawer (open, close, Escape) are
 * NOT here: they are the inline script at the end of the section.
 *
 * No external framework; vanilla JS, ES2015+. Defer-loaded from
 * sections/main-pickld-collection.liquid.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------
     1. Show more / show fewer toggle
     ------------------------------------------------------------ */
  function initShowMore(scope) {
    (scope || document).querySelectorAll('[data-pickld-filter-showmore]').forEach(function (btn) {
      if (btn.dataset.pickldBound === '1') return;
      btn.dataset.pickldBound = '1';

      btn.addEventListener('click', function () {
        var group = btn.closest('.pickld-collection__filter-group');
        if (!group) return;
        var hiddenItems = group.querySelectorAll('[data-showmore-target]');
        var expanded = btn.dataset.expanded === 'true';
        var next = !expanded;

        hiddenItems.forEach(function (li) {
          li.classList.toggle('is-hidden', !next);
        });

        btn.dataset.expanded = String(next);
        btn.textContent = next
          ? (btn.dataset.lessLabel || '− Show fewer')
          : (btn.dataset.moreLabel || '+ Show all');
      });
    });
  }

  /* ------------------------------------------------------------
     2. AJAX Apply filters
     ------------------------------------------------------------ */
  function getSectionId(form) {
    // The template renders inside the main section; grab its id from
    // the parent shopify-section wrapper. We look up once per submit.
    var wrapper = form.closest('[id^="shopify-section-"]');
    if (!wrapper) return null;
    return wrapper.id.replace(/^shopify-section-/, '');
  }

  function serialiseForm(form) {
    // Build a URLSearchParams from the form so we get proper filter
    // param names (including repeated ?filter.p.tag=X&filter.p.tag=Y).
    var data = new FormData(form);
    var params = new URLSearchParams();
    data.forEach(function (value, key) {
      if (value === '' || value == null) return;
      params.append(key, value);
    });
    return params;
  }

  /*
    No opacity loading fade at all. The View Transitions API in swapSection
    handles cross-fade natively where supported. Height pinning below
    prevents the grid from collapsing between old and new content.
  */
  function setLoading() { /* no-op - kept for API compatibility with popstate handler */ }

  /*
    Swap the fetched section into the live DOM WITHOUT removing containers.
    Previous version used replaceWith() on entire elements which briefly
    unmounted them; the "blank for a bit" perception came from that + a
    full-form innerHTML re-render on the sidebar. New approach:

      - Grid: replace inner HTML of .pickld-collection__grid-wrap (container stays).
      - Count / pagination: update textContent only.
      - Filter form: update per-filter COUNT text + input.disabled state only.
        Never touch input.checked (that's the user's canonical intent - the
        URL is source of truth, we don't overwrite it).
  */
  /*
    Perform the in-place DOM updates. Wrapped in a View Transitions
    call site so supported browsers (Chrome/Edge/Safari 18+) render a
    native cross-fade between old and new grid state. In unsupported
    browsers the fallback is an instant swap - no blank-flash either
    because we pin the wrap height and update contents inside it.
  */
  function doSwap(doc) {
    var incomingWrap = doc.querySelector('.pickld-collection__grid-wrap');
    var currentWrap = document.querySelector('.pickld-collection__grid-wrap');
    if (incomingWrap && currentWrap) {
      currentWrap.innerHTML = incomingWrap.innerHTML;
    }

    var incomingCount = doc.querySelector('.pickld-collection__count');
    var currentCount = document.querySelector('.pickld-collection__count');
    if (incomingCount && currentCount) {
      currentCount.textContent = incomingCount.textContent;
    }

    var incomingPag = doc.querySelector('.pickld-collection__pagination');
    var currentPag = document.querySelector('.pickld-collection__pagination');
    if (incomingPag && currentPag) {
      currentPag.innerHTML = incomingPag.innerHTML;
    } else if (incomingPag && !currentPag && currentWrap) {
      currentWrap.parentNode.appendChild(incomingPag.cloneNode(true));
    } else if (!incomingPag && currentPag) {
      currentPag.remove();
    }

    var incomingCounts = doc.querySelectorAll('#CollectionFiltersForm .pickld-collection__filter-count');
    var currentCounts = document.querySelectorAll('#CollectionFiltersForm .pickld-collection__filter-count');
    incomingCounts.forEach(function (inc, i) {
      if (currentCounts[i]) currentCounts[i].textContent = inc.textContent;
    });

    var incomingInputs = doc.querySelectorAll('#CollectionFiltersForm input[type=checkbox]');
    var currentInputs = document.querySelectorAll('#CollectionFiltersForm input[type=checkbox]');
    incomingInputs.forEach(function (inc, i) {
      if (currentInputs[i]) currentInputs[i].disabled = inc.disabled;
    });
  }

  function swapSection(newHtml) {
    var doc = new DOMParser().parseFromString(newHtml, 'text/html');
    var currentWrap = document.querySelector('.pickld-collection__grid-wrap');

    /*
      Pin the grid height BEFORE the swap so the page doesn't reflow when
      the new grid has fewer products. Cleared after the next paint so
      the wrap can naturally grow if the new grid is taller.
    */
    var beforeHeight = currentWrap ? currentWrap.getBoundingClientRect().height : 0;
    if (currentWrap && beforeHeight) currentWrap.style.minHeight = beforeHeight + 'px';

    var unpinHeight = function () {
      requestAnimationFrame(function () {
        if (currentWrap) currentWrap.style.minHeight = '';
      });
    };

    if (typeof document.startViewTransition === 'function') {
      /*
        View Transitions API - the browser snapshots the current DOM,
        applies our mutation, then cross-fades between the snapshots.
        Zero JS animation code. Chrome/Edge 111+, Safari 18+.

        Wrapped in try/catch: an aborted transition (e.g. a second filter
        change firing before the first one settles) throws synchronously
        in some browsers rather than rejecting the .finished promise, and
        that must never surface as an InvalidStateError - fall back to a
        plain swap instead.
      */
      try {
        var transition = document.startViewTransition(function () { doSwap(doc); });
        transition.finished.then(unpinHeight).catch(unpinHeight);
      } catch (e) {
        doSwap(doc);
        unpinHeight();
      }
    } else {
      doSwap(doc);
      unpinHeight();
    }
  }

  function applyFilters(form) {
    var sectionId = getSectionId(form);
    if (!sectionId) {
      // Section-rendering path unavailable; fall back to a real submit.
      form.submit();
      return;
    }

    var params = serialiseForm(form);
    var browserUrl = window.location.pathname + '?' + params.toString();
    var fetchUrl = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&' + params.toString();

    setLoading(true);

    fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        swapSection(html);
        // Update the URL so the state is shareable + back-button works.
        window.history.pushState({ pickldFilters: true }, '', browserUrl);
        setLoading(false);
        // Move focus back to the product count for accessibility.
        var count = document.querySelector('.pickld-collection__count');
        if (count) count.setAttribute('tabindex', '-1');
      })
      .catch(function () {
        setLoading(false);
        // Silent fallback - if AJAX blows up, the button's default
        // submit would have worked. Let the user click again.
      });
  }

  /* Debounce utility - coalesces rapid changes into one AJAX call. */
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function initApplyForm() {
    var form = document.getElementById('CollectionFiltersForm');
    if (!form || form.dataset.pickldBound === '1') return;
    form.dataset.pickldBound = '1';

    // Explicit submit still works (accessibility + no-JS fallback if this
    // handler ever fails to attach).
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      applyFilters(form);
    });

    // Instant apply - 2026-07-03: firing AJAX on change/input removes the
    // "scroll down to Apply, then back up" friction Ben flagged. The Apply
    // button is hidden via .is-instant-mode CSS once we bind. Debounce
    // coalesces multiple rapid checkbox clicks into a single request; the
    // price inputs get a longer debounce so users can finish typing a value.
    var debouncedApplyFast = debounce(function () { applyFilters(form); }, 100);
    var debouncedApplySlow = debounce(function () { applyFilters(form); }, 600);

    /*
      Pickld 2026-07-03: Skill Level filter routes to sub-collections instead
      of applying as a query param. Business reason: /collections/beginner-paddles,
      /collections/intermediate-paddles and /collections/professional-pickleball-paddles
      are SEO landing pages Google already crawls. Sending users to those URLs
      when they filter by skill level keeps the URL clean AND compounds the
      SEO investment. Other active filter params (Brand, Shape, Play style,
      Thickness) are preserved on navigation so users don't lose their state.
    */
    var SKILL_COLLECTION_HANDLES = {
      'Starter': 'beginner-paddles',
      'Intermediate': 'intermediate-paddles',
      'Professional': 'professional-pickleball-paddles'
    };

    function routeSkillLevel(target) {
      if (!target || target.type !== 'checkbox') return false;
      if (target.name !== 'filter.p.m.custom.skill_tier') return false;
      // The visible label text is the human-readable value ("Intermediate")
      // regardless of what Shopify's checkbox value ends up being for a
      // metafield reference.
      var labelSpan = target.closest('label').querySelector('.pickld-collection__filter-text > span:first-child');
      var label = labelSpan ? labelSpan.textContent.trim() : '';
      var handle = SKILL_COLLECTION_HANDLES[label];
      if (!handle) return false;

      // Preserve other active filter params, drop any existing skill_tier
      // param (the collection itself scopes to that skill level).
      var params = new URLSearchParams(window.location.search);
      Array.from(params.keys()).forEach(function (k) {
        if (k === 'filter.p.m.custom.skill_tier') params.delete(k);
      });
      var qs = params.toString();
      window.location.href = '/collections/' + handle + (qs ? '?' + qs : '');
      return true;
    }

    form.addEventListener('change', function (event) {
      var target = event.target;
      if (!target) return;
      if (target.type === 'checkbox' || target.type === 'radio') {
        // Skill Level values route to sub-collections; other filters AJAX.
        if (target.checked && routeSkillLevel(target)) return;
        debouncedApplyFast();
      }
    });

    form.addEventListener('input', function (event) {
      var target = event.target;
      if (!target) return;
      if (target.type === 'number' || target.type === 'text') {
        debouncedApplySlow();
      }
    });

    form.classList.add('is-instant-mode');
  }

  function onPopState(event) {
    if (!event.state || !event.state.pickldFilters) return;
    // User hit back/forward - re-run the AJAX apply for the new URL.
    var form = document.getElementById('CollectionFiltersForm');
    if (!form) return;
    var sectionId = getSectionId(form);
    if (!sectionId) return;
    var fetchUrl = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) + window.location.search;
    setLoading(true);
    fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) { swapSection(html); setLoading(false); })
      .catch(function () { setLoading(false); });
  }

  /* ------------------------------------------------------------
     Boot
     ------------------------------------------------------------ */
  function boot() {
    initShowMore(document);
    initApplyForm();
    window.addEventListener('popstate', onPopState);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
